import { flexRender, type Header } from '@tanstack/react-table';

import { TableHeaderCell } from '@myn/mui';

import type { OrderingType } from '../../store/atoms/TableSorting/tableSortingStore.types.js';
import type { GenericTableWrapperType } from './PermissionsTable.jsx';

type SortOrder = {
  orderingBE?: string;
  ordering?: OrderingType;
  field?: string;
};

const getIsSorted = (
  fieldMeta?: string,
  field?: string,
  ordering?: OrderingType,
) => {
  if (field !== fieldMeta) {
    return null;
  }

  if (ordering === 'asc') {
    return true;
  }

  if (ordering === 'desc') {
    return false;
  }

  return null;
};

const getSortOrder = (
  fieldMeta?: string,
  field?: string,
  ordering?: OrderingType,
): SortOrder => {
  if (fieldMeta !== field) {
    return { orderingBE: fieldMeta, ordering: 'asc', field: fieldMeta };
  }

  if (!fieldMeta) {
    return { orderingBE: '', field: fieldMeta };
  }

  if (ordering === 'asc') {
    return { orderingBE: `-${fieldMeta}`, ordering: 'desc', field };
  }

  if (ordering === 'desc') {
    return { orderingBE: '', field: fieldMeta };
  }

  return { orderingBE: fieldMeta, ordering: 'asc', field: fieldMeta };
};

type Props<T extends GenericTableWrapperType> = {
  header: Header<T, unknown>;
  field?: string;
  ordering?: OrderingType;
  dataIsLoading?: boolean;
  setField: (field?: string) => void;
  setOrdering: (ordering?: OrderingType) => void;
  setOrderingBE: (orderingBE?: string) => void;
};

const PermissionsTableHeaderCell = <T extends GenericTableWrapperType>({
  header,
  field,
  ordering,
  dataIsLoading,
  setField,
  setOrdering,
  setOrderingBE,
}: Props<T>) => {
  const fieldMeta = header?.column?.columnDef?.meta?.field;

  const handleSort = () => {
    // prevent sorting when data is loading
    if (dataIsLoading) {
      return;
    }

    const {
      ordering: normalizedOrdering,
      orderingBE: normalizedOrderingBE,
      field: normalizedField,
    } = getSortOrder(fieldMeta, field, ordering);

    setField(normalizedField);
    setOrdering(normalizedOrdering);
    setOrderingBE(normalizedOrderingBE);
  };

  return (
    <TableHeaderCell
      onSort={
        header?.column?.columnDef.enableSorting !== false
          ? handleSort
          : undefined
      }
      sorted={getIsSorted(fieldMeta, field, ordering)}
      align="left"
    >
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
    </TableHeaderCell>
  );
};

export default PermissionsTableHeaderCell;
